import React, { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Form, Modal, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import axios from 'axios';
import { baseURL } from '../../../../helpers/baseURL';
import { getRequestErrorMessage, headers } from '../../../../helpers/Requests';
import successAlert from '../../../../helpers/successAlert';
import failAlert from '../../../../helpers/failAlert';
import keywordBadgeStyle from '../../../../helpers/keywordBadgeStyle';

const emptyForm = {
    name: '',
    ai_prompt: '',
    keywords: [],
    match_type: 'any',
    destination_workspace_id: '',
    email_ids: [],
    is_active: true,
};

// Used for both Create and Edit — pass `rule` to edit, omit (or null) to create.
const AutoTransferRuleModal = ({ show, onHide, rule, onSaved }) => {
    const isEdit = !!rule;
    const [form, setForm] = useState(emptyForm);
    const [keywordInput, setKeywordInput] = useState('');
    const [destinations, setDestinations] = useState([]);
    const [destinationsLoading, setDestinationsLoading] = useState(false);
    const [inboxes, setInboxes] = useState([]);
    const [inboxesLoading, setInboxesLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!show) return;

        setForm(
            rule
                ? {
                      name: rule.name || '',
                      ai_prompt: rule.ai_prompt || '',
                      keywords: Array.isArray(rule.keywords) ? rule.keywords : [],
                      match_type: rule.match_type || 'any',
                      destination_workspace_id: rule.destination_workspace_id || '',
                      email_ids: Array.isArray(rule.email_ids)
                          ? rule.email_ids
                          : (Array.isArray(rule.emails) ? rule.emails.map((email) => email.id) : []),
                      is_active: rule.is_active !== false,
                  }
                : emptyForm
        );
        setKeywordInput('');
        setError('');

        let cancelled = false;
        const loadDestinations = async () => {
            setDestinationsLoading(true);
            try {
                const { data } = await axios.get(`${baseURL}ticket-transfer-targets`, headers);
                if (!cancelled) {
                    setDestinations(Array.isArray(data?.workspaces) ? data.workspaces : []);
                }
            } catch (err) {
                if (!cancelled) {
                    failAlert(err?.response?.data?.message ?? 'Failed to load destination workspaces.');
                    setDestinations([]);
                }
            } finally {
                if (!cancelled) setDestinationsLoading(false);
            }
        };
        const loadInboxes = async () => {
            setInboxesLoading(true);
            try {
                const { data } = await axios.get(`${baseURL}emails`, headers);
                const list = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
                if (!cancelled) {
                    setInboxes(list);
                }
            } catch (err) {
                if (!cancelled) {
                    failAlert(err?.response?.data?.message ?? 'Failed to load incoming emails.');
                    setInboxes([]);
                }
            } finally {
                if (!cancelled) setInboxesLoading(false);
            }
        };
        loadDestinations();
        loadInboxes();
        return () => {
            cancelled = true;
        };
    }, [show, rule]);

    // Any edit clears the standing error. A duplicate-name message that outlives the
    // name it complained about reads as a live failure, even though saving now works.
    const setField = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
        setError('');
    };

    const addKeyword = () => {
        const trimmed = keywordInput.trim();
        if (!trimmed) return;
        if (form.keywords.some((k) => k.toLowerCase() === trimmed.toLowerCase())) {
            setKeywordInput('');
            return;
        }
        setForm((prev) => ({ ...prev, keywords: [...prev.keywords, trimmed] }));
        setKeywordInput('');
        setError('');
    };

    const removeKeyword = (keyword) => {
        setForm((prev) => ({ ...prev, keywords: prev.keywords.filter((k) => k !== keyword) }));
        setError('');
    };

    const handleKeywordKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            addKeyword();
        }
    };

    const inboxOptions = useMemo(
        () =>
            inboxes.map((email) => ({
                value: Number(email.id),
                label: email.email_address || email.name || `#${email.id}`,
            })),
        [inboxes]
    );

    const selectedInboxes = inboxOptions.filter((option) =>
        form.email_ids.map(Number).includes(option.value)
    );

    const submit = async (e) => {
        e.preventDefault();
        // Every field is checked here rather than with `required`, so validation
        // always appears in one styled place instead of half native browser bubbles
        // (which fire first and would make these checks unreachable) and half inline.
        if (!form.name.trim()) {
            setError('Give this rule a name.');
            return;
        }
        if (form.ai_prompt.trim().length < 10) {
            setError('Describe what should route to this workspace, in at least 10 characters.');
            return;
        }
        if (!form.destination_workspace_id) {
            setError('Choose a destination workspace.');
            return;
        }

        setSaving(true);
        setError('');
        try {
            const payload = {
                name: form.name.trim(),
                ai_prompt: form.ai_prompt.trim(),
                keywords: form.keywords,
                match_type: form.match_type || 'any',
                destination_workspace_id: Number(form.destination_workspace_id),
                email_ids: form.email_ids,
                is_active: form.is_active,
            };

            const { data } = isEdit
                ? await axios.post(`${baseURL}auto-transfer-rule/${rule.id}`, payload, headers)
                : await axios.post(`${baseURL}auto-transfer-rules`, payload, headers);

            successAlert(isEdit ? 'Auto-transfer rule updated' : 'Auto-transfer rule created');
            onSaved?.(data?.rule);
            onHide();
        } catch (err) {
            setError(getRequestErrorMessage(err, 'Failed to save auto-transfer rule.'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Form onSubmit={submit}>
                <Modal.Header closeButton>
                    <Modal.Title>{isEdit ? 'Edit auto-transfer rule' : 'Create auto-transfer rule'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Rule name</Form.Label>
                        <Form.Control
                            value={form.name}
                            onChange={(e) => setField('name', e.target.value)}
                            placeholder="e.g. Support"
                            maxLength={255}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>What should route here?</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={form.ai_prompt}
                            onChange={(e) => setField('ai_prompt', e.target.value)}
                            placeholder={
                                'e.g. Analyze the content and see if the person is in need of some kind of support, ' +
                                'even if they don’t use the word "help" — phrases like "I need someone" or ' +
                                '"I’m in need of something" should count too. Upon a match, send it to this workspace.'
                            }
                            maxLength={2000}
                        />
                        <Form.Text className="text-muted">
                            AI reads every eligible ticket's content against this description to decide if it belongs here.
                            This is the main way rules are matched.
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Keywords (optional)</Form.Label>
                        <div className="d-flex gap-2">
                            <Form.Control
                                value={keywordInput}
                                onChange={(e) => setKeywordInput(e.target.value)}
                                onKeyDown={handleKeywordKeyDown}
                                placeholder="Type a keyword and press Enter"
                                maxLength={100}
                            />
                            <Button type="button" variant="outline-secondary" onClick={addKeyword}>
                                Add
                            </Button>
                        </div>
                        {form.keywords.length > 0 && (
                            <div className="d-flex flex-wrap gap-2 mt-2">
                                {form.keywords.map((keyword) => (
                                    <Badge
                                        key={keyword}
                                        bg="secondary"
                                        className="d-inline-flex align-items-center gap-1"
                                        style={keywordBadgeStyle}
                                    >
                                        {keyword}
                                        <button
                                            type="button"
                                            onClick={() => removeKeyword(keyword)}
                                            aria-label={`Remove ${keyword}`}
                                            style={{
                                                border: 'none',
                                                background: 'transparent',
                                                color: 'inherit',
                                                lineHeight: 1,
                                                padding: 0,
                                                marginLeft: 2,
                                            }}
                                        >
                                            ×
                                        </button>
                                    </Badge>
                                ))}
                            </div>
                        )}
                        <Form.Text className="text-muted d-block mt-2">
                            Passed to the AI as extra context, and shown in Test Rule / logs when they appear in a ticket.
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Incoming emails (optional)</Form.Label>
                        {inboxesLoading ? (
                            <div className="py-2">
                                <Spinner animation="border" size="sm" />
                            </div>
                        ) : (
                            <Select
                                isMulti
                                isClearable
                                closeMenuOnSelect={false}
                                options={inboxOptions}
                                value={selectedInboxes}
                                onChange={(selected) =>
                                    setField('email_ids', (selected || []).map((option) => Number(option.value)))
                                }
                                placeholder="All incoming emails"
                                // An empty menu has three different causes and only one of them
                                // is "this workspace has no inboxes" — react-select also empties
                                // it when every option is already picked, or when a search
                                // matches nothing. Saying the workspace has none is alarming
                                // mid-setup, and false in two of the three cases.
                                noOptionsMessage={({ inputValue }) => {
                                    if (inboxOptions.length === 0) {
                                        return 'No incoming emails in this workspace';
                                    }
                                    return inputValue
                                        ? 'No incoming email matches that search'
                                        : 'Every incoming email is already selected';
                                }}
                                classNamePrefix="select-component"
                                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                menuPosition="fixed"
                                styles={{
                                    menuPortal: (base) => ({ ...base, zIndex: 1060 }),
                                    control: (base) => ({ ...base, fontSize: '14px' }),
                                }}
                            />
                        )}
                        <Form.Text className="text-muted">
                            Leave empty to run this rule for every new ticket. If you pick one or more addresses,
                            it only applies when the ticket arrives at those inboxes.
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Transfer to</Form.Label>
                        {destinationsLoading ? (
                            <div className="py-2">
                                <Spinner animation="border" size="sm" />
                            </div>
                        ) : (
                            <Form.Select
                                value={form.destination_workspace_id}
                                onChange={(e) => setField('destination_workspace_id', e.target.value)}
                            >
                                <option value="">Select a workspace…</option>
                                {destinations.map((ws) => (
                                    <option key={ws.id} value={ws.id} disabled={ws.can_transfer === false}>
                                        {ws.name}
                                        {ws.can_transfer === false ? ' (transfer paused)' : ''}
                                    </option>
                                ))}
                            </Form.Select>
                        )}
                        {destinations.length === 0 && !destinationsLoading ? (
                            <Form.Text className="text-muted">
                                No connected workspaces yet — connect a transfer code first.
                            </Form.Text>
                        ) : null}
                    </Form.Group>

                    <Form.Group>
                        <Form.Check
                            type="switch"
                            id="auto-transfer-rule-active"
                            label="Active"
                            checked={form.is_active}
                            onChange={(e) => setField('is_active', e.target.checked)}
                        />
                    </Form.Group>

                    {error ? (
                        <div className="text-danger small mt-3" role="alert">
                            {error}
                        </div>
                    ) : null}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide}>
                        Cancel
                    </Button>
                    <Button type="submit" variant="primary" disabled={saving}>
                        {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create Rule'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default AutoTransferRuleModal;
